import { useMemo, useState } from "react";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { api } from "../lib/api";
import { useResource, formatDate, formatTime, titleCase } from "../lib/hooks";
import { uploadFile, fileViewUrl } from "../lib/files";
import { Modal } from "../components/Modal";
import { EntityForm } from "../components/EntityForm";
import { ParticipantPhotoCard } from "../components/ParticipantPhotoCard";
import { StringListCard } from "../components/StringListCard";
import { FormFillDialog } from "../components/FormFillDialog";
import { FundingCard } from "./Participants";
import type {
    FormSubmission, FormTemplate, Handover, Medication, Participant,
    PartnerDocument, ServiceNote, Shift,
} from "../lib/types";

type Tab = "overview" | "roster" | "done" | "notes" | "medications" | "handovers" | "docs" | "risk" | "forms" | "funding" | "invoices" | "settings";

const MED_ROUTES = [
    { value: "oral", label: "Oral" },
    { value: "topical", label: "Topical" },
    { value: "subcutaneous", label: "Subcutaneous" },
    { value: "intramuscular", label: "Intramuscular" },
    { value: "intravenous", label: "Intravenous" },
    { value: "peg", label: "PEG" },
    { value: "inhaled", label: "Inhaled" },
    { value: "other", label: "Other" },
];

interface RegistrationRow {
    id: string;
    first_name: string;
    last_name: string;
    status: "pending" | "approved" | "rejected";
    decision_note: string | null;
    created_at: string;
}

interface OurCarePlan {
    id: string;
    file_name: string;
    created_at: string;
    shift_starts_at: string;
}

/**
 * The partner window: a provider or support coordinator sees their own
 * clients, who we have allocated to them, what is scheduled and what has been
 * done. Every list here is scoped by row-level security to participants linked
 * to the partner's organisation — this component never filters by company,
 * because it never receives anyone else's rows to filter.
 */
export const PartnerClients = () => {
    const participants = useResource<Participant[]>("/participants");
    const shifts = useResource<Shift[]>("/shifts");
    const notes = useResource<ServiceNote[]>("/service-notes");
    const medications = useResource<Medication[]>("/medications");
    const handovers = useResource<Handover[]>("/handovers");
    const partnerDocs = useResource<PartnerDocument[]>("/partner-documents");
    const registrations = useResource<RegistrationRow[]>("/participant-registrations");
    const formTemplates = useResource<FormTemplate[]>("/form-templates");
    const formSubmissions = useResource<FormSubmission[]>("/form-submissions");

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [tab, setTab] = useState<Tab>("overview");
    const [uploading, setUploading] = useState<null | "care_plan" | "consent" | "risk_assessment">(null);
    const [registering, setRegistering] = useState(false);
    const [addingMed, setAddingMed] = useState(false);
    const [ceasingMed, setCeasingMed] = useState<Medication | null>(null);
    const [addingHandover, setAddingHandover] = useState(false);
    const [editingClient, setEditingClient] = useState(false);
    const [fillingTemplate, setFillingTemplate] = useState<FormTemplate | null>(null);
    const [requestingWorker, setRequestingWorker] = useState(false);
    const [comment, setComment] = useState("");
    const [commentBusy, setCommentBusy] = useState(false);
    const [commentError, setCommentError] = useState<string | null>(null);

    // Register-a-participant: submitted for TrustCare review — an approved
    // registration becomes a participant linked to this organisation.
    const registrationUi = (
        <>
            {registering && (
                <EntityForm
                    title="Register a participant" path="/participant-registrations"
                    subtitle="Goes to the TrustCare intake team for review. Once approved, the participant appears under My clients with their roster, notes and documents."
                    fields={[
                        { name: "firstName", label: "First name", kind: "text", required: true, half: true },
                        { name: "lastName", label: "Last name", kind: "text", required: true, half: true },
                        { name: "ndisNumber", label: "NDIS number", kind: "text", half: true },
                        { name: "dateOfBirth", label: "Date of birth", kind: "date", half: true },
                        { name: "phone", label: "Phone", kind: "tel", half: true },
                        { name: "suburb", label: "Suburb", kind: "text", half: true },
                        { name: "services", label: "Services needed", kind: "multiselect",
                          optionsFrom: { path: "/referral-services", addLabel: "Add service" } },
                        { name: "notes", label: "Anything we should know", kind: "textarea",
                          help: "Care needs, current arrangements, urgency. Please wait for our request before sending health documents." },
                    ]}
                    onClose={() => setRegistering(false)}
                    onSaved={() => { setRegistering(false); registrations.reload(); }} />
            )}
            {(registrations.data ?? []).length > 0 && (
                <div className="card">
                    <div className="card-head"><h2>Your registrations</h2></div>
                    <div className="card-body" style={{ display: "grid", gap: "0.4rem" }}>
                        {(registrations.data ?? []).map((r) => (
                            <div key={r.id} className="file-row">
                                <span className="mono">{formatDate(r.created_at)}</span>
                                <span style={{ fontWeight: 600 }}>{r.first_name} {r.last_name}</span>
                                <span className={`chip ${r.status === "approved" ? "pass" : r.status === "rejected" ? "block" : "warn"}`}>
                                    {titleCase(r.status)}
                                </span>
                                {r.status === "rejected" && r.decision_note && (
                                    <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{r.decision_note}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    const clients = participants.data ?? [];
    const selected = clients.find((p) => p.id === selectedId) ?? clients[0] ?? null;

    const now = Date.now();
    const clientShifts = useMemo(
        () => (shifts.data ?? [])
            .filter((s) => s.participant_id === selected?.id && s.status !== "cancelled")
            .sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
        [shifts.data, selected?.id]
    );
    const upcoming = clientShifts.filter((s) => new Date(s.ends_at).getTime() >= now);
    const delivered = clientShifts.filter((s) => new Date(s.ends_at).getTime() < now).reverse();
    const clientNotes = (notes.data ?? [])
        .filter((n) => n.participant_id === selected?.id)
        .sort((a, b) => b.note_date.localeCompare(a.note_date));

    /**
     * The nurses and workers this client actually sees, from the roster —
     * with a compliance yes/no per person. The flag is a single boolean from
     * the server; which certificate is behind it stays internal.
     */
    const team = useMemo(() => {
        const people = new Map<string, { count: number; compliant: boolean }>();
        for (const shift of clientShifts) {
            if (!shift.employee_name) continue;
            const entry = people.get(shift.employee_name) ?? { count: 0, compliant: shift.employee_compliant ?? false };
            entry.count += 1;
            entry.compliant = shift.employee_compliant ?? entry.compliant;
            people.set(shift.employee_name, entry);
        }
        return [...people.entries()].sort((a, b) => b[1].count - a[1].count);
    }, [clientShifts]);

    const allClientDocs = (partnerDocs.data ?? []).filter((d) => d.participant_id === selected?.id);
    const clientDocs = allClientDocs.filter((d) => d.kind !== "risk_assessment");
    const riskDocs = allClientDocs.filter((d) => d.kind === "risk_assessment");
    const ourPlans = useResource<OurCarePlan[]>(selected ? `/participants/${selected.id}/care-plans` : null);
    const clientInvoices = useResource<Array<{
        id: string; invoice_number: string; status: string;
        period_start: string; period_end: string; total: string;
    }>>(selected ? `/participants/${selected.id}/invoices` : null);

    const clientMeds = (medications.data ?? []).filter((m) => m.participant_id === selected?.id);
    const clientHandovers = (handovers.data ?? [])
        .filter((h) => h.participant_id === selected?.id)
        .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
    // A row this organisation added: the only kind a partner may cease. The
    // server RLS enforces it regardless — this just hides buttons that would 403.
    const isOwnMed = (m: Medication) => m.added_by_company_id != null;

    const publishedTemplates = (formTemplates.data ?? []).filter((t) => t.status === "published");
    const templateById = new Map((formTemplates.data ?? []).map((t) => [t.id, t]));
    // RLS already limits this list to the partner's own submissions.
    const clientForms = (formSubmissions.data ?? [])
        .filter((s) => s.participant_id === selected?.id)
        .sort((a, b) => b.created_at.localeCompare(a.created_at));

    if (participants.error) return <div className="error-banner">{participants.error}</div>;

    if (!participants.loading && clients.length === 0) {
        return (
            <div style={{ display: "grid", gap: "1rem" }}>
                {registrationUi}
                <div className="card">
                    <div className="card-body" style={{ textAlign: "center", padding: "2.5rem" }}>
                        <h3 style={{ marginBottom: "0.4rem" }}>No clients linked yet</h3>
                        <p style={{ color: "var(--muted)", fontSize: "0.88rem" }}>
                            Your organisation has no participants linked to it in our records.
                            Register your participant below and our intake team will review it —
                            or message us and we will connect existing clients to this portal.
                        </p>
                        <button className="btn" style={{ marginTop: "0.8rem" }} onClick={() => setRegistering(true)}>
                            + Register participant
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="partner-grid">
            <div style={{ display: "grid", gap: "1rem", alignSelf: "start", alignContent: "start" }}>
            {registrationUi}
            <div className="card" style={{ alignSelf: "start" }}>
                <div className="card-head">
                    <h2>Your clients</h2>
                    <button className="btn small" onClick={() => setRegistering(true)}>+ Register</button>
                </div>
                <div className="card-body flush">
                    {clients.map((client) => (
                        <button key={client.id}
                            className={`client-row ${selected?.id === client.id ? "on" : ""}`}
                            onClick={() => setSelectedId(client.id)}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                <InitialsAvatar name={client.full_name} size={26} />
                                {client.full_name}
                            </span>
                            <span className={`chip ${client.status === "active" ? "pass" : "mute"}`}>
                                {titleCase(client.status)}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
            </div>

            {selected && (
                <div style={{ display: "grid", gap: "1rem", alignContent: "start", minWidth: 0 }}>
                    <div className="card">
                        <div className="card-head">
                            <div>
                                <h2 style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                    <InitialsAvatar name={selected.full_name} size={32}
                                        photoUrl={selected.photo_file_id ? fileViewUrl(selected.photo_file_id) : null} />
                                    {selected.full_name}
                                </h2>
                                <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: 2 }}>
                                    {selected.ndis_number && <span className="mono">NDIS {selected.ndis_number} · </span>}
                                    {selected.date_of_birth && <>DOB {formatDate(selected.date_of_birth)} · </>}
                                    {selected.service_supports.join(", ") || "No supports listed"}
                                </p>
                            </div>
                            <button className="btn small" onClick={() => setRequestingWorker(true)}
                                title="Ask TrustCare to roster a worker — allocation stays with our team">
                                Request worker
                            </button>
                        </div>
                        {team.length > 0 && (
                            <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)" }}>
                                <div className="doc-label" style={{ marginBottom: "0.4rem" }}>Allocated team</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                                    {team.map(([name, info]) => (
                                        <span key={name}
                                            className={`chip ${info.compliant ? "pass" : "warn"}`}
                                            title={info.compliant
                                                ? "Fully compliant — all required competencies current"
                                                : "A required competency needs renewal — they will not be rostered until it is"}>
                                            {name} {info.compliant ? "✓" : "•"}
                                        </span>
                                    ))}
                                </div>
                                <div style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: "0.35rem" }}>
                                    ✓ fully compliant — every competency their role requires is current.
                                </div>
                            </div>
                        )}
                    </div>

                    {selected.important_alert && (
                        <div className="error-banner" style={{ background: "#FCF3E3", borderColor: "#E0B75B", color: "#7A5B12" }}>
                            <b>Important:</b> {selected.important_alert}
                        </div>
                    )}

                    <div className="tabs">
                        <button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")}>
                            Overview
                        </button>
                        <button className={tab === "roster" ? "active" : ""} onClick={() => setTab("roster")}>
                            Upcoming roster ({upcoming.length})
                        </button>
                        <button className={tab === "done" ? "active" : ""} onClick={() => setTab("done")}>
                            Work done ({delivered.length})
                        </button>
                        <button className={tab === "notes" ? "active" : ""} onClick={() => setTab("notes")}>
                            Notes ({clientNotes.length})
                        </button>
                        <button className={tab === "medications" ? "active" : ""} onClick={() => setTab("medications")}>
                            Medications ({clientMeds.filter((m) => m.status === "active").length})
                        </button>
                        <button className={tab === "handovers" ? "active" : ""} onClick={() => setTab("handovers")}>
                            Handovers ({clientHandovers.length})
                        </button>
                        <button className={tab === "docs" ? "active" : ""} onClick={() => setTab("docs")}>
                            Documents ({clientDocs.length + (ourPlans.data?.length ?? 0)})
                        </button>
                        <button className={tab === "risk" ? "active" : ""} onClick={() => setTab("risk")}>
                            Risk assessment ({riskDocs.length})
                        </button>
                        <button className={tab === "forms" ? "active" : ""} onClick={() => setTab("forms")}>
                            Forms ({clientForms.length})
                        </button>
                        <button className={tab === "funding" ? "active" : ""} onClick={() => setTab("funding")}>
                            Funding
                        </button>
                        <button className={tab === "invoices" ? "active" : ""} onClick={() => setTab("invoices")}>
                            Invoices ({(clientInvoices.data ?? []).length})
                        </button>
                        <button className={tab === "settings" ? "active" : ""} onClick={() => setTab("settings")}>
                            Settings
                        </button>
                    </div>

                    {tab === "forms" && (
                        <>
                            {fillingTemplate && (
                                <FormFillDialog template={fillingTemplate}
                                    participantId={selected.id} participantName={selected.full_name}
                                    onClose={() => setFillingTemplate(null)}
                                    onDone={() => { setFillingTemplate(null); formSubmissions.reload(); }} />
                            )}
                            <div className="card">
                                <div className="card-head"><h2>Start a form</h2></div>
                                <div className="card-body" style={{ display: "grid", gap: "0.8rem" }}>
                                    {[...new Set(publishedTemplates.map((t) => t.category ?? "Other"))].sort().map((category) => (
                                        <div key={category}>
                                            <div className="doc-label" style={{ marginBottom: "0.35rem" }}>{category}</div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                                                {publishedTemplates
                                                    .filter((t) => (t.category ?? "Other") === category)
                                                    .map((t) => (
                                                        <button key={t.id} className="btn ghost small"
                                                            onClick={() => setFillingTemplate(t)}>
                                                            {t.name}
                                                        </button>
                                                    ))}
                                            </div>
                                        </div>
                                    ))}
                                    {publishedTemplates.length === 0 && (
                                        <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No forms are available yet.</p>
                                    )}
                                </div>
                            </div>
                            <div className="card">
                                <div className="card-head"><h2>Forms you have submitted</h2></div>
                                <div className="card-body flush">
                                    <div className="table-wrap">
                                        <table>
                                            <thead><tr><th>Date</th><th>Form</th><th>Summary</th><th>Status</th></tr></thead>
                                            <tbody>
                                                {clientForms.map((s) => {
                                                    const t = templateById.get(s.template_id);
                                                    return (
                                                        <tr key={s.id}>
                                                            <td className="mono">{formatDate(s.created_at)}</td>
                                                            <td style={{ fontWeight: 600 }}>{t ? t.name : "—"}</td>
                                                            <td style={{ color: "var(--ink-soft)" }}>{s.summary}</td>
                                                            <td><span className={`chip ${s.status === "reviewed" ? "pass" : "warn"}`}>
                                                                {s.status === "reviewed" ? "Reviewed by TrustCare" : "Awaiting review"}
                                                            </span></td>
                                                        </tr>
                                                    );
                                                })}
                                                {clientForms.length === 0 && (
                                                    <tr><td colSpan={4} className="empty">
                                                        Nothing submitted yet — start a form above and it goes straight to the TrustCare team.
                                                    </td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {tab === "funding" && (
                        <FundingCard participantId={selected.id} canManage={false} />
                    )}

                    {requestingWorker && (
                        <EntityForm
                            title={`Request a worker — ${selected.full_name}`} path="/partner-requests"
                            subtitle="Goes to the TrustCare team as a tracked request (TC-REQ). We roster the worker and confirm back — allocation always stays with TrustCare."
                            fixed={{ kind: "worker_request", participantId: selected.id }}
                            fields={[
                                { name: "subject", label: "What do you need", kind: "text", required: true,
                                  placeholder: "e.g. RN for Tuesday and Thursday mornings" },
                                { name: "details", label: "Details", kind: "textarea", required: true,
                                  help: "Days and times, skills needed, anything the roster team should know." },
                                { name: "services", label: "Services", kind: "multiselect",
                                  optionsFrom: { path: "/referral-services" } },
                                { name: "urgency", label: "Urgency", kind: "select", half: true, options: [
                                    { value: "routine", label: "Routine" },
                                    { value: "high", label: "High" },
                                    { value: "urgent", label: "Urgent" },
                                ] },
                            ]}
                            onClose={() => setRequestingWorker(false)}
                            onSaved={() => setRequestingWorker(false)} />
                    )}

                    {tab === "settings" && (
                        <div className="grid cols-2">
                            <ParticipantPhotoCard participant={selected}
                                onSaved={() => participants.reload()} />
                            <StringListCard title="Default shift tasks"
                                help="What every TrustCare worker should do at each of this client's shifts. Seeded onto new shifts automatically."
                                items={selected.default_shift_tasks ?? []}
                                addLabel="Add task" placeholder="e.g. Check pressure areas"
                                onSave={async (items) => {
                                    await api.put(`/participants/${selected.id}`, { default_shift_tasks: items });
                                    participants.reload();
                                }} />
                            <div className="card">
                                <div className="card-head"><h2>Client record</h2></div>
                                <div className="card-body">
                                    <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginBottom: "0.6rem" }}>
                                        Update {selected.full_name}'s details — identity, contact, disability and plan
                                        information, care and safety notes. Changes appear to the TrustCare team
                                        immediately. Status, provider link and billing settings are managed by TrustCare.
                                    </p>
                                    <button className="btn" onClick={() => setEditingClient(true)}>Edit details</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {editingClient && (
                        <EntityForm
                            title={`Edit ${selected.full_name}`} path="/participants"
                            initial={selected}
                            fields={[
                                { name: "first_name", label: "First name", kind: "text", required: true, half: true },
                                { name: "last_name", label: "Last name", kind: "text", required: true, half: true },
                                { name: "date_of_birth", label: "Date of birth", kind: "date", half: true },
                                { name: "ndis_number", label: "NDIS number", kind: "text", half: true },
                                { name: "primary_phone", label: "Phone", kind: "tel", half: true },
                                { name: "primary_email", label: "Email", kind: "email", half: true },
                                { name: "address", label: "Address", kind: "text" },
                                { name: "suburb", label: "Suburb", kind: "text", half: true },
                                { name: "postcode", label: "Postcode", kind: "text", half: true },
                                { name: "primary_contact", label: "Primary contact", kind: "text", half: true },
                                { name: "primary_disability", label: "Primary disability", kind: "select", half: true,
                                  optionsFrom: { path: "/disability-types" } },
                                { name: "secondary_disability", label: "Secondary disability", kind: "select", half: true,
                                  optionsFrom: { path: "/disability-types" } },
                                { name: "plan_managed_by", label: "Plan managed by", kind: "select", half: true, options: [
                                    { value: "self_managed", label: "Self managed" },
                                    { value: "plan_managed", label: "Plan managed" },
                                    { value: "ndia_managed", label: "NDIA managed" },
                                ] },
                                { name: "gender", label: "Gender", kind: "select", half: true, options: [
                                    { value: "female", label: "Female" },
                                    { value: "male", label: "Male" },
                                    { value: "non_binary", label: "Non-binary" },
                                    { value: "prefer_not_to_say", label: "Prefer not to say" },
                                    { value: "other", label: "Other" },
                                ] },
                                { name: "quick_snapshot", label: "Quick snapshot", kind: "textarea",
                                  help: "One paragraph a worker reads first — who this person is at a glance." },
                                { name: "public_guardian", label: "Public guardian", kind: "text", half: true },
                                { name: "plan_nominee", label: "Plan nominee", kind: "text", half: true },
                                { name: "conditions", label: "Conditions", kind: "multiselect",
                                  optionsFrom: { path: "/participant-conditions" } },
                                { name: "service_supports", label: "Service supports", kind: "multiselect",
                                  options: ["Core", "COS", "Allied Health", "Capacity Building", "SIL", "Complex nursing"]
                                      .map((s) => ({ value: s, label: s })) },
                                { name: "external_coordinator_name", label: "External coordinator — name", kind: "text", half: true },
                                { name: "external_coordinator_company", label: "External coordinator — company", kind: "text", half: true },
                                { name: "external_coordinator_email", label: "External coordinator — email", kind: "email", half: true },
                                { name: "external_coordinator_phone", label: "External coordinator — phone", kind: "tel", half: true },
                                { name: "plan_start_date", label: "Plan start", kind: "date", half: true },
                                { name: "plan_end_date", label: "Plan end", kind: "date", half: true },
                                { name: "next_of_kin_name", label: "Next of kin — name", kind: "text", half: true },
                                { name: "next_of_kin_relationship", label: "Next of kin — relationship", kind: "text", half: true },
                                { name: "next_of_kin_phone", label: "Next of kin — phone", kind: "tel", half: true },
                                { name: "household_pets", label: "Pets at the home", kind: "text", half: true },
                                { name: "behaviour_notes", label: "Behaviour notes", kind: "textarea" },
                                { name: "access_notes", label: "Home access notes", kind: "textarea" },
                                { name: "important_alert", label: "Important alert", kind: "text",
                                  help: "Shown as a banner on the record everywhere. Max 256 characters." },
                                { name: "shift_instructions", label: "Shift instructions", kind: "textarea" },
                            ]}
                            onClose={() => setEditingClient(false)}
                            onSaved={() => { setEditingClient(false); participants.reload(); }} />
                    )}

                    {tab === "overview" && (
                        <div className="grid cols-2">
                            <div className="card">
                                <div className="card-head"><h2>Details</h2></div>
                                <div className="card-body">
                                    {selected.quick_snapshot && (
                                        <p style={{ fontSize: "0.86rem", color: "var(--ink-soft)", lineHeight: 1.55,
                                            borderBottom: "1px solid var(--line-soft)", paddingBottom: "0.6rem", marginBottom: "0.6rem" }}>
                                            {selected.quick_snapshot}
                                        </p>
                                    )}
                                    <dl className="kv">
                                        <dt>Date of birth</dt><dd>{formatDate(selected.date_of_birth)}</dd>
                                        <dt>Gender</dt><dd>{selected.gender ? titleCase(selected.gender) : "—"}</dd>
                                        <dt>NDIS number</dt><dd className="mono">{selected.ndis_number ?? "—"}</dd>
                                        <dt>Address</dt><dd>{[selected.address, selected.suburb, selected.postcode].filter(Boolean).join(", ") || "—"}</dd>
                                        <dt>Phone</dt><dd>{selected.primary_phone ?? "—"}</dd>
                                        <dt>Primary contact</dt><dd>{selected.primary_contact ?? "—"}</dd>
                                        <dt>Primary disability</dt><dd>{selected.primary_disability ?? "—"}</dd>
                                        <dt>Secondary disability</dt><dd>{selected.secondary_disability ?? "—"}</dd>
                                        <dt>Plan managed by</dt><dd>{selected.plan_managed_by ? titleCase(selected.plan_managed_by) : "—"}</dd>
                                        <dt>Public guardian</dt><dd>{selected.public_guardian ?? "—"}</dd>
                                        <dt>Plan nominee</dt><dd>{selected.plan_nominee ?? "—"}</dd>
                                        <dt>Conditions</dt>
                                        <dd>
                                            {(selected.conditions ?? []).length > 0
                                                ? <span style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
                                                    {(selected.conditions ?? []).map((c) => <span key={c} className="chip warn">{c}</span>)}
                                                  </span>
                                                : "—"}
                                        </dd>
                                        <dt>Plan period</dt>
                                        <dd>
                                            {selected.plan_start_date
                                                ? `${formatDate(selected.plan_start_date)} – ${selected.plan_end_date ? formatDate(selected.plan_end_date) : "ongoing"}`
                                                : "—"}
                                        </dd>
                                        <dt>Service period</dt>
                                        <dd>
                                            {selected.service_start_date
                                                ? `${formatDate(selected.service_start_date)} – ${selected.service_end_date ? formatDate(selected.service_end_date) : "ongoing"}`
                                                : "—"}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                            <div style={{ display: "grid", gap: "1rem", alignContent: "start" }}>
                                <div className="card">
                                    <div className="card-head"><h2>Care &amp; safety</h2></div>
                                    <div className="card-body">
                                        <dl className="kv">
                                            <dt>Next of kin</dt>
                                            <dd>
                                                {selected.next_of_kin_name
                                                    ? `${selected.next_of_kin_name}${selected.next_of_kin_relationship ? ` (${selected.next_of_kin_relationship})` : ""}${selected.next_of_kin_phone ? ` · ${selected.next_of_kin_phone}` : ""}`
                                                    : "—"}
                                            </dd>
                                            <dt>Pets at the home</dt><dd>{selected.household_pets ?? "—"}</dd>
                                            <dt>Behaviour</dt><dd>{selected.behaviour_notes ?? "—"}</dd>
                                            <dt>Home access</dt><dd>{selected.access_notes ?? "—"}</dd>
                                            <dt>Shift instructions</dt><dd>{selected.shift_instructions ?? "—"}</dd>
                                        </dl>
                                    </div>
                                </div>
                                <div className="card">
                                    <div className="card-head"><h2>Goals, likes &amp; dislikes</h2></div>
                                    <div className="card-body" style={{ display: "grid", gap: "0.55rem" }}>
                                        <div>
                                            <div className="doc-label">Goals</div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", marginTop: "0.25rem" }}>
                                                {selected.goals.length > 0
                                                    ? selected.goals.map((g) => <span key={g} className="chip pass">{g}</span>)
                                                    : <span style={{ color: "var(--muted)", fontSize: "0.82rem" }}>—</span>}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="doc-label">Likes</div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", marginTop: "0.25rem" }}>
                                                {selected.likes.length > 0
                                                    ? selected.likes.map((l) => <span key={l} className="chip info">{l}</span>)
                                                    : <span style={{ color: "var(--muted)", fontSize: "0.82rem" }}>—</span>}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="doc-label">Dislikes</div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", marginTop: "0.25rem" }}>
                                                {selected.dislikes.length > 0
                                                    ? selected.dislikes.map((d) => <span key={d} className="chip block">{d}</span>)
                                                    : <span style={{ color: "var(--muted)", fontSize: "0.82rem" }}>—</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === "risk" && (
                        <div className="card">
                            <div className="card-head">
                                <h2>Risk assessments</h2>
                                <button className="btn small" onClick={() => setUploading("risk_assessment")}>+ Add risk assessment</button>
                            </div>
                            <div className="card-body flush">
                                <div className="table-wrap">
                                    <table>
                                        <tbody>
                                            {riskDocs.map((doc) => (
                                                <tr key={doc.id}>
                                                    <td style={{ fontWeight: 600 }}>
                                                        <a href={fileViewUrl(doc.file_id)} target="_blank" rel="noreferrer">{doc.file_name}</a>
                                                    </td>
                                                    <td className="mono">{formatDate(doc.created_at)}</td>
                                                    <td style={{ color: "var(--muted)" }}>{doc.note ?? ""}</td>
                                                </tr>
                                            ))}
                                            {riskDocs.length === 0 && (
                                                <tr><td className="empty">
                                                    No risk assessments yet — upload your own; TrustCare's are shared here as they are prepared.
                                                </td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)", fontSize: "0.75rem", color: "var(--muted)" }}>
                                You can add your own risk assessments. Ones prepared by TrustCare cannot be edited or removed from here.
                            </div>
                        </div>
                    )}

                    {tab === "invoices" && (
                        <div className="card">
                            <div className="card-head"><h2>Invoices for {selected.full_name}</h2></div>
                            <div className="card-body flush">
                                <div className="table-wrap">
                                    <table>
                                        <thead><tr><th>Invoice</th><th>Period</th><th>Total</th><th>Status</th></tr></thead>
                                        <tbody>
                                            {(clientInvoices.data ?? []).map((i) => (
                                                <tr key={i.id}>
                                                    <td className="mono" style={{ fontWeight: 700 }}>{i.invoice_number}</td>
                                                    <td className="mono">{formatDate(i.period_start)} – {formatDate(i.period_end)}</td>
                                                    <td className="mono">${Number(i.total).toLocaleString("en-AU")}</td>
                                                    <td><span className={`chip ${i.status === "paid" ? "pass" : "info"}`}>{titleCase(i.status)}</span></td>
                                                </tr>
                                            ))}
                                            {(clientInvoices.data ?? []).length === 0 && (
                                                <tr><td colSpan={4} className="empty">
                                                    Nothing invoiced for {selected.full_name}'s shifts yet. The full account view is under Invoices in the menu.
                                                </td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {(tab === "roster" || tab === "done") && (
                        <div className="card">
                            <div className="card-body flush">
                                <div className="table-wrap">
                                    <table>
                                        <thead>
                                            <tr><th>Date</th><th>Time</th><th>Worker</th><th>Role</th><th>Status</th></tr>
                                        </thead>
                                        <tbody>
                                            {(tab === "roster" ? upcoming : delivered).map((shift) => (
                                                <tr key={shift.id}>
                                                    <td className="mono">{formatDate(shift.starts_at)}</td>
                                                    <td className="mono">{formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}</td>
                                                    <td>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                            <InitialsAvatar name={shift.employee_name} size={24} />
                                                            {shift.employee_name ?? "To be allocated"}
                                                        </div>
                                                    </td>
                                                    <td>{shift.role_name ?? "—"}</td>
                                                    <td>
                                                        <span className={`chip ${shift.status === "completed" ? "pass" : "info"}`}>
                                                            {tab === "done" && shift.status === "confirmed" ? "delivered" : shift.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {(tab === "roster" ? upcoming : delivered).length === 0 && (
                                                <tr><td colSpan={5} className="empty">
                                                    {tab === "roster" ? "Nothing scheduled ahead." : "No delivered shifts yet."}
                                                </td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === "medications" && (
                        <div className="card">
                            <div className="card-head">
                                <h2>Medications</h2>
                                <button className="btn small" onClick={() => setAddingMed(true)}>+ Add medication</button>
                            </div>
                            <div className="card-body flush">
                                <div className="table-wrap">
                                    <table>
                                        <thead>
                                            <tr><th>Medication</th><th>Dose</th><th>Frequency</th><th>Status</th><th>Source</th><th /></tr>
                                        </thead>
                                        <tbody>
                                            {clientMeds.map((m) => (
                                                <tr key={m.id}>
                                                    <td style={{ fontWeight: 600 }}>
                                                        {m.name}
                                                        {m.is_prn && <span className="chip warn" style={{ marginLeft: 6 }}>PRN</span>}
                                                    </td>
                                                    <td>{m.dose} · {m.route}</td>
                                                    <td>{m.frequency}</td>
                                                    <td>
                                                        <span className={`chip ${m.status === "active" ? "pass" : m.status === "ceased" ? "mute" : "warn"}`}>
                                                            {titleCase(m.status)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {isOwnMed(m)
                                                            ? m.verified_at
                                                                ? <span className="chip pass" title="TrustCare has checked this entry against the chart.">Verified</span>
                                                                : <span className="chip warn" title="Waiting for TrustCare to verify this entry.">Added by you — unverified</span>
                                                            : <span className="chip info">TrustCare</span>}
                                                    </td>
                                                    <td>
                                                        {isOwnMed(m) && m.status === "active" && (
                                                            <button className="icon-btn danger" onClick={() => setCeasingMed(m)}>Cease</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {clientMeds.length === 0 && (
                                                <tr><td colSpan={6} className="empty">
                                                    No medications recorded — add what {selected.full_name} is taking and our team will verify it.
                                                </td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)", fontSize: "0.75rem", color: "var(--muted)" }}>
                                You can add medications and cease the ones you added. Entries recorded by TrustCare
                                can only be changed by our team — message us if something looks wrong.
                            </div>
                        </div>
                    )}

                    {tab === "handovers" && (
                        <div className="card">
                            <div className="card-head">
                                <h2>Handovers</h2>
                                <button className="btn small" onClick={() => setAddingHandover(true)}>+ Add handover</button>
                            </div>
                            <div className="card-body" style={{ display: "grid", gap: "0.85rem" }}>
                                {clientHandovers.map((h) => (
                                    <div key={h.id} style={{ borderLeft: "3px solid var(--accent)", paddingLeft: "0.8rem" }}>
                                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                            <span style={{ fontSize: "0.75rem", color: "var(--muted)" }} className="mono">{formatDate(h.occurred_at)}</span>
                                            <span className={`chip ${h.status === "acknowledged" ? "pass" : "info"}`}>{titleCase(h.status)}</span>
                                            {h.added_by_company_id && <span className="chip warn">Added by you</span>}
                                        </div>
                                        <p style={{ fontSize: "0.85rem", color: "var(--ink-soft)", marginTop: "0.3rem", lineHeight: 1.55 }}>{h.summary}</p>
                                        {h.risks && <p style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}><b>Risks:</b> {h.risks}</p>}
                                        {h.actions_required && <p style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}><b>Actions:</b> {h.actions_required}</p>}
                                    </div>
                                ))}
                                {clientHandovers.length === 0 && (
                                    <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                                        No handovers yet — add one to pass information to the TrustCare team before a shift.
                                    </p>
                                )}
                            </div>
                            <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)", fontSize: "0.75rem", color: "var(--muted)" }}>
                                Handovers are a permanent record — they cannot be edited or deleted once added, by you or by us.
                            </div>
                        </div>
                    )}

                    {addingMed && (
                        <EntityForm
                            title={`Add medication — ${selected.full_name}`} path="/medications"
                            subtitle="Goes onto the medication list immediately, marked as added by your organisation until TrustCare verifies it."
                            fixed={{ participant_id: selected.id }}
                            fields={[
                                { name: "name", label: "Medication name", kind: "text", required: true, half: true },
                                { name: "dose", label: "Dose", kind: "text", required: true, half: true, placeholder: "e.g. 5 mg" },
                                { name: "route", label: "Route", kind: "select", options: MED_ROUTES, half: true },
                                { name: "frequency", label: "Frequency", kind: "text", required: true, half: true, placeholder: "e.g. Twice daily with food" },
                                { name: "is_prn", label: "PRN (as needed)", kind: "checkbox" },
                                { name: "prn_instructions", label: "PRN instructions", kind: "textarea",
                                  help: "Required if PRN — when to give it, maximum doses, when to escalate." },
                                { name: "prescriber", label: "Prescriber", kind: "text", half: true },
                                { name: "started_on", label: "Started on", kind: "date", half: true },
                                { name: "notes", label: "Notes", kind: "textarea" },
                            ]}
                            onClose={() => setAddingMed(false)}
                            onSaved={() => { setAddingMed(false); medications.reload(); }} />
                    )}

                    {ceasingMed && (
                        <EntityForm
                            title={`Cease ${ceasingMed.name}`} path="/medications"
                            subtitle="The medication stays on the record as ceased — nothing is deleted."
                            initial={ceasingMed}
                            fixed={{ status: "ceased" }}
                            fields={[
                                { name: "ceased_on", label: "Ceased on", kind: "date", required: true },
                                { name: "ceased_reason", label: "Reason", kind: "textarea", required: true,
                                  placeholder: "e.g. Ceased by GP at review on 2 Aug" },
                            ]}
                            onClose={() => setCeasingMed(null)}
                            onSaved={() => { setCeasingMed(null); medications.reload(); }} />
                    )}

                    {addingHandover && (
                        <EntityForm
                            title={`Add handover — ${selected.full_name}`} path="/handovers"
                            subtitle="Passes information to the TrustCare team. Handovers are permanent and cannot be edited after saving."
                            fixed={{ participant_id: selected.id, status: "issued" }}
                            fields={[
                                { name: "summary", label: "Summary", kind: "textarea", required: true,
                                  placeholder: "What the incoming worker needs to know." },
                                { name: "clinical_changes", label: "Clinical changes", kind: "textarea" },
                                { name: "risks", label: "Risks", kind: "textarea" },
                                { name: "actions_required", label: "Actions required", kind: "textarea" },
                            ]}
                            onClose={() => setAddingHandover(false)}
                            onSaved={() => { setAddingHandover(false); handovers.reload(); }} />
                    )}

                    {tab === "docs" && (
                        <div style={{ display: "grid", gap: "1rem" }}>
                            <div className="card">
                                <div className="card-head">
                                    <h2>Your uploads</h2>
                                    <div style={{ display: "flex", gap: "0.4rem" }}>
                                        <button className="btn small" onClick={() => setUploading("care_plan")}>+ Care plan</button>
                                        <button className="btn ghost small" onClick={() => setUploading("consent")}>+ Consent</button>
                                    </div>
                                </div>
                                <div className="card-body flush">
                                    <div className="table-wrap">
                                        <table>
                                            <tbody>
                                                {clientDocs.map((doc) => (
                                                    <tr key={doc.id}>
                                                        <td><span className="chip info">{titleCase(doc.kind)}</span></td>
                                                        <td style={{ fontWeight: 600 }}>
                                                            <a href={fileViewUrl(doc.file_id)} target="_blank" rel="noreferrer">{doc.file_name}</a>
                                                        </td>
                                                        <td className="mono">{formatDate(doc.created_at)}</td>
                                                        <td style={{ color: "var(--muted)" }}>{doc.note ?? ""}</td>
                                                    </tr>
                                                ))}
                                                {clientDocs.length === 0 && (
                                                    <tr><td className="empty">
                                                        Nothing uploaded yet — care plans and signed consents you upload here go straight to our team.
                                                    </td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="card">
                                <div className="card-head"><h2>Care plans we have prepared</h2></div>
                                <div className="card-body flush">
                                    <div className="table-wrap">
                                        <table>
                                            <tbody>
                                                {(ourPlans.data ?? []).map((plan) => (
                                                    <tr key={plan.id}>
                                                        <td style={{ fontWeight: 600 }}>
                                                            <a href={fileViewUrl(plan.id)} target="_blank" rel="noreferrer">{plan.file_name}</a>
                                                        </td>
                                                        <td className="mono">for shift {formatDate(plan.shift_starts_at)}</td>
                                                    </tr>
                                                ))}
                                                {(ourPlans.data ?? []).length === 0 && (
                                                    <tr><td className="empty">No TrustCare care plans on file for this client yet.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {uploading && selected && (
                        <UploadDialog
                            kind={uploading}
                            participant={selected}
                            onClose={() => setUploading(null)}
                            onDone={() => { setUploading(null); partnerDocs.reload(); }}
                        />
                    )}

                    {tab === "notes" && (
                        <div className="card">
                            <div className="card-body" style={{ display: "grid", gap: "0.85rem" }}>
                                {clientNotes.map((note) => (
                                    <div key={note.id} style={{ borderLeft: "3px solid var(--accent)", paddingLeft: "0.8rem" }}>
                                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                            <strong style={{ fontSize: "0.88rem" }}>{note.subject}</strong>
                                            {note.kind === "partner_comment"
                                                ? <span className="chip warn">Your comment</span>
                                                : <span className="chip info">{titleCase(note.kind)}</span>}
                                            <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{formatDate(note.note_date)}</span>
                                        </div>
                                        <p style={{ fontSize: "0.85rem", color: "var(--ink-soft)", marginTop: "0.3rem", lineHeight: 1.55 }}>
                                            {note.body}
                                        </p>
                                    </div>
                                ))}
                                {clientNotes.length === 0 && (
                                    <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No notes recorded yet.</p>
                                )}
                            </div>
                            <div className="card-body" style={{ borderTop: "1px solid var(--line-soft)", display: "grid", gap: "0.45rem" }}>
                                {commentError && <div className="error-banner">{commentError}</div>}
                                <div className="field" style={{ marginBottom: 0 }}>
                                    <label htmlFor="pc-comment">Add a comment</label>
                                    <textarea id="pc-comment" value={comment}
                                        placeholder="Visible to the TrustCare team on this client's notes. TrustCare's notes cannot be edited."
                                        onChange={(e) => setComment(e.target.value)} />
                                </div>
                                <div>
                                    <button className="btn small" disabled={commentBusy || comment.trim().length < 5}
                                        onClick={() => void (async () => {
                                            setCommentBusy(true);
                                            setCommentError(null);
                                            try {
                                                await api.post("/service-notes", {
                                                    participant_id: selected.id, kind: "partner_comment",
                                                    subject: "Provider comment", body: comment.trim(),
                                                });
                                                setComment("");
                                                notes.reload();
                                            } catch (err) {
                                                setCommentError(err instanceof Error ? err.message : "Could not add the comment");
                                            } finally {
                                                setCommentBusy(false);
                                            }
                                        })()}>
                                        {commentBusy ? "Posting…" : "Post comment"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


const UPLOAD_TITLES: Record<string, string> = {
    care_plan: "Upload care plan",
    consent: "Upload consent",
    risk_assessment: "Upload risk assessment",
};

const UploadDialog = ({ kind, participant, onClose, onDone }: {
    kind: "care_plan" | "consent" | "risk_assessment";
    participant: Participant;
    onClose: () => void;
    onDone: () => void;
}) => {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const send = async (file: File) => {
        setBusy(true);
        setError(null);
        try {
            const stored = await uploadFile(file, "general");
            await api.post("/partner-documents", {
                fileId: stored.id, kind, participantId: participant.id,
                note: note.trim() || undefined,
            });
            onDone();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Upload failed");
            setBusy(false);
        }
    };

    return (
        <Modal
            title={`${UPLOAD_TITLES[kind]} — ${participant.full_name}`}
            subtitle="Visible to the TrustCare team immediately."
            onClose={onClose}
        >
            {error && <div className="error-banner">{error}</div>}
            <div className="field">
                <label htmlFor="pd-note">Note (optional)</label>
                <input id="pd-note" value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder={kind === "consent" ? "e.g. Signed NDIS consent, valid to June 2027" : "e.g. Updated PBSP v3"} />
            </div>
            <div className="field">
                <label htmlFor="pd-file">Document (PDF preferred, max 10 MB)</label>
                <input id="pd-file" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={busy}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void send(f); }} />
                {busy && <span className="help">Uploading…</span>}
            </div>
        </Modal>
    );
};
